/**
 * Authenticated SaaS route metadata (Account, Hub, Studio, Calendar, settings).
 * Titles and descriptions are short and purpose-first so a merchant knows
 * what each screen is for at a glance (document title + meta description).
 */

import type { PageMetaEntry } from './types';

export const APP_PAGE_META: readonly PageMetaEntry[] = [
  // ── Account hub (?tab=) ─────────────────────────────────────────────────
  {
    path: '/account',
    tab: 'billing',
    title: 'Billing & Plans',
    description: 'Plan, payments, and credits for this workspace.',
  },
  {
    path: '/account',
    tab: 'integrations',
    title: 'Integrations',
    description: 'Connect Shopify, Meta, email, ads, and other tools.',
  },
  {
    path: '/account',
    tab: 'brand',
    title: 'Brand DNA',
    description: 'Voice, identity, team, and messaging for your brand.',
  },
  {
    path: '/account',
    tab: 'support',
    title: 'Support',
    description: 'Get help and browse common account questions.',
  },
  {
    path: '/account',
    tab: 'profile',
    title: 'Account Settings',
    description: 'Profile, security, notifications, and workspace preferences.',
  },
  {
    path: '/account',
    title: 'Account Settings',
    description: 'Profile, brand, billing, and integrations for this workspace.',
  },

  // ── Business Hub ────────────────────────────────────────────────────────
  {
    path: '/business',
    tab: 'contacts',
    title: 'Contacts',
    description: 'People, customers, and contact records for this workspace.',
  },
  {
    path: '/business',
    tab: 'inventory',
    title: 'Inventory',
    description: 'Catalog, pricing, and inventory in one place.',
  },
  {
    path: '/business',
    tab: 'orders',
    title: 'Orders',
    description: 'Track orders, bookings, and transactions.',
  },
  {
    path: '/business',
    tab: 'ads',
    title: 'Ads',
    description: 'Meta and Google Ads performance for this workspace.',
  },
  {
    path: '/business',
    tab: 'finance',
    title: 'Finance',
    description: 'P&L, expenses, and tax in one place.',
  },
  {
    path: '/business',
    tab: 'calendar',
    title: 'Business Calendar',
    description: 'Appointments and scheduled sends for this business.',
  },
  {
    path: '/business',
    tab: 'work',
    title: 'My Work',
    description: 'Board rows and chat tasks assigned to you.',
  },
  {
    path: '/business',
    tab: 'funnels',
    title: 'Funnel Library',
    description: 'Ready-made automations — review before you switch them on.',
  },
  {
    path: '/business',
    tab: 'team',
    title: 'Team Members',
    description: 'Invite teammates and manage roles for this workspace.',
  },
  {
    path: '/business',
    tab: 'overview',
    title: 'Business Hub',
    description: 'KPIs, customers, and growth activity for your business.',
  },
  {
    path: '/business/customers',
    title: 'Customers',
    description: 'Identity, purchases, and engagement for every contact.',
  },
  {
    path: '/business/contacts',
    title: 'Contacts',
    description: 'Browse and open contact records for this workspace.',
  },
  {
    path: '/business/orders',
    title: 'Orders',
    description: 'Track orders, costs, and profit per order.',
  },
  {
    path: '/business/products',
    title: 'Products',
    description: 'Catalog, pricing, and inventory in one place.',
  },
  {
    path: '/business/suppliers',
    title: 'Suppliers',
    description: 'Partners you buy from and restock with.',
  },
  {
    path: '/business',
    title: 'Business Hub',
    description: 'KPIs, customers, and growth activity for your business.',
  },

  // ── Studio / automations ────────────────────────────────────────────────
  {
    path: '/studio',
    tab: 'my-flows',
    title: 'My Automations',
    description: 'Your active email and chat automation flows.',
  },
  {
    path: '/studio',
    tab: 'campaigns',
    title: 'Campaigns',
    description: 'Create and review email campaigns.',
  },
  {
    path: '/studio',
    tab: 'canvas',
    title: 'Flow Canvas',
    description: 'Wire any API to any action on a visual canvas.',
  },
  {
    path: '/studio',
    tab: 'chat',
    title: 'Chat Automations',
    description: 'Chat and messaging automation tools.',
  },
  {
    path: '/studio',
    tab: 'broadcasts',
    title: 'Broadcasts',
    description: 'Send one message to everyone who has opted in.',
  },
  {
    path: '/studio',
    tab: 'templates',
    title: 'Automation Templates',
    description: 'Starter templates for email and chat automations.',
  },
  {
    path: '/studio',
    tab: 'agents',
    title: 'Agents',
    description: 'Chat agents connected to your inbox.',
  },
  {
    path: '/studio',
    tab: 'business-packs',
    title: 'Starter Packs',
    description: 'Install starter automation packs for your business kind.',
  },
  {
    path: '/studio/flow',
    title: 'Flow Editor',
    description: 'Edit an automation flow on the canvas.',
  },
  {
    path: '/studio/automation',
    title: 'Automation Detail',
    description: 'Details and settings for one automation.',
  },
  {
    path: '/studio',
    title: 'Automations',
    description:
      'Email, chat, and popups — triggers, flows, and conversations in one place.',
  },
  {
    path: '/automations/lab',
    title: 'Automations Lab',
    description: 'Connect anything to anything. Rules run — no AI at runtime.',
  },
  {
    path: '/automations',
    title: 'Automations',
    description:
      'Email, chat, and popups — triggers, flows, and conversations in one place.',
  },
  {
    path: '/agent/canvas',
    title: 'Agent Canvas',
    description: 'Design agent conversation flows on the canvas.',
  },

  // ── Calendar / social ───────────────────────────────────────────────────
  {
    path: '/calendar',
    tab: 'composer',
    title: 'Compose Post',
    description: 'Compose and schedule social posts.',
  },
  {
    path: '/calendar',
    tab: 'replies',
    title: 'Comment Rules',
    description: 'Automate comment replies across social channels.',
  },
  {
    path: '/calendar',
    tab: 'performance',
    title: 'Social Performance',
    description: 'Reach, engagement, and comments for your posts.',
  },
  {
    path: '/calendar',
    tab: 'buzzpage',
    title: 'BuzzPage',
    description: 'Manage your link-in-bio BuzzPage.',
  },
  {
    path: '/calendar',
    title: 'Calendar',
    description: 'Bookings, posts, campaigns, and sends — scheduled in one place.',
  },
  {
    path: '/marketing/social',
    tab: 'calendar',
    title: 'Social Calendar',
    description: 'Social content calendar for posts and reels.',
  },
  {
    path: '/marketing/social',
    title: 'Social Automator',
    description: 'Schedule posts, manage replies, and publish across channels.',
  },
  {
    path: '/marketing',
    title: 'Social Automator',
    description: 'Schedule posts, manage replies, and publish across channels.',
  },

  // ── Inbox / chat / other ────────────────────────────────────────────────
  {
    path: '/inbox',
    title: 'Inbox',
    description: 'WhatsApp, Instagram, Messenger, and email — reply in one place.',
  },
  {
    path: '/chat',
    title: 'Workspace',
    description: 'Team chat, docs, and member lessons in one workspace.',
  },
  {
    path: '/popups',
    title: 'Popups',
    description: 'Website popup campaigns for leads and offers.',
  },
  {
    path: '/knowledge-base',
    title: 'Knowledge Base',
    description: 'Articles used by automations and support.',
  },
  {
    path: '/privacy-center',
    title: 'Privacy Center',
    description: 'Export, delete, or control how your data is used.',
  },
  {
    path: '/assistant/confirm',
    title: 'Confirm Action',
    description: 'Confirm a pending assistant action.',
  },

  // ── Settings ────────────────────────────────────────────────────────────
  {
    path: '/settings/brand/team',
    title: 'Team Members',
    description: 'Invite and manage team members for your brand.',
  },
  {
    path: '/settings/brand/assets',
    title: 'Brand Assets',
    description: 'Product and brand assets for messaging.',
  },
  {
    path: '/settings/brand',
    title: 'Brand Settings',
    description: 'Voice, identity, team, and messaging for your brand.',
  },
  {
    path: '/settings/billing',
    title: 'Billing & Plans',
    description: 'Plan, payments, and credits for this workspace.',
  },
  {
    path: '/settings/connected-apps',
    title: 'Connected Apps',
    description: 'OAuth apps connected to your account.',
  },
  {
    path: '/settings/custom-domain',
    title: 'Custom Domain',
    description: 'Custom domain for your public pages.',
  },
  {
    path: '/settings/white-label',
    title: 'White Label',
    description: 'White-label branding for this workspace.',
  },
  {
    path: '/settings/email-domain',
    title: 'Email Domain',
    description: 'Authenticate your sending domain for email.',
  },
  {
    path: '/settings/integrations/shopify',
    title: 'Shopify Integration',
    description: 'Connect and configure Shopify for this workspace.',
  },
  {
    path: '/settings/integrations/meta',
    title: 'Meta Integration',
    description: 'Connect Instagram, Facebook, and WhatsApp via Meta.',
  },
  {
    path: '/settings/integrations/tiktok',
    title: 'TikTok Integration',
    description: 'Connect TikTok to this workspace.',
  },
  {
    path: '/settings/integrations/linkedin',
    title: 'LinkedIn Integration',
    description: 'Connect LinkedIn to this workspace.',
  },
  {
    path: '/settings/integrations/youtube',
    title: 'YouTube Integration',
    description: 'Connect YouTube to this workspace.',
  },
  {
    path: '/settings/integrations/x',
    title: 'X Integration',
    description: 'Connect X (Twitter) to this workspace.',
  },
  {
    path: '/settings/integrations/hubspot',
    title: 'HubSpot Integration',
    description: 'Connect HubSpot CRM to this workspace.',
  },
  {
    path: '/settings/integrations/salesforce',
    title: 'Salesforce Integration',
    description: 'Connect Salesforce to this workspace.',
  },
  {
    path: '/settings/integrations/google-sheets',
    title: 'Google Sheets Integration',
    description: 'Connect Google Sheets to this workspace.',
  },
  {
    path: '/settings/integrations/google-ads',
    title: 'Google Ads Integration',
    description: 'Connect Google Ads to this workspace.',
  },
  {
    path: '/settings/integrations/gmail',
    title: 'Gmail Integration',
    description: 'Connect Gmail to this workspace.',
  },
  {
    path: '/settings/integrations/tiktok-ads',
    title: 'TikTok Ads Integration',
    description: 'Connect TikTok Ads to this workspace.',
  },
];
